/**
 * @file LabTATChart.tsx
 * @description Recharts area chart showing Lab Turnaround Time trends
 *              (average + P90) over the course of the day.
 */

import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { MOCK_TAT_TREND } from "../../data/mockData";

interface DataPoint {
  time: string;
  avg_tat: number;
  p90_tat: number;
}

interface LabTATChartProps {
  data?: DataPoint[];
}

// Custom tooltip
function CustomTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div
      className="rounded-xl px-4 py-3 text-xs shadow-card"
      style={{
        background: "rgba(7, 20, 40, 0.98)",
        border: "1px solid rgba(255,255,255,0.12)",
      }}
    >
      <div className="text-gray-400 mb-2 font-mono">{label}</div>
      {payload.map((p) => (
        <div key={p.name} className="flex items-center gap-2 mb-1">
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: p.color }} />
          <span className="text-gray-300">{p.name}:</span>
          <span className="text-white font-bold">{p.value} min</span>
        </div>
      ))}
    </div>
  );
}

export function LabTATChart({ data = MOCK_TAT_TREND }: LabTATChartProps) {
  return (
    <div
      className="rounded-2xl border border-white/8 p-6"
      style={{
        background: "linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.015) 100%)",
      }}
    >
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-white font-semibold text-base">Lab TAT Trend</h2>
          <p className="text-gray-500 text-xs mt-0.5">Turnaround time in minutes — today</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="w-6 h-0.5 bg-teal-400 rounded-full" />
            <span className="text-xs text-gray-500">Avg TAT</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-6 h-0.5 bg-orange-400 rounded-full" />
            <span className="text-xs text-gray-500">P90 TAT</span>
          </div>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={220}>
        <AreaChart data={data} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
          <defs>
            <linearGradient id="gradTeal" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.25} />
              <stop offset="95%" stopColor="#14b8a6" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gradOrange" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#f97316" stopOpacity={0.2} />
              <stop offset="95%" stopColor="#f97316" stopOpacity={0} />
            </linearGradient>
          </defs>

          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
          <XAxis
            dataKey="time"
            tick={{ fill: "#6b7280", fontSize: 11 }}
            axisLine={{ stroke: "rgba(255,255,255,0.06)" }}
            tickLine={false}
          />
          <YAxis
            tick={{ fill: "#6b7280", fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            unit=" min"
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={{ display: "none" }} />

          <Area
            type="monotone"
            dataKey="avg_tat"
            name="Avg TAT"
            stroke="#14b8a6"
            strokeWidth={2}
            fill="url(#gradTeal)"
            dot={false}
            activeDot={{ r: 5, fill: "#14b8a6", stroke: "#071428", strokeWidth: 2 }}
          />
          <Area
            type="monotone"
            dataKey="p90_tat"
            name="P90 TAT"
            stroke="#f97316"
            strokeWidth={2}
            fill="url(#gradOrange)"
            dot={false}
            activeDot={{ r: 5, fill: "#f97316", stroke: "#071428", strokeWidth: 2 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
